import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Flight, FlightStatus, SmsType, WhatsappSettings } from 'src/app/core/graphql/generated/gen-types';
import { BookingService } from 'src/app/core/services/booking/booking.service';
import { CrewService } from 'src/app/core/services/crew/crew.service';
import { FlightService } from 'src/app/core/services/flight/flight.service';
import { SmsService } from 'src/app/core/services/sms/sms.service';
import { TenantSettingService } from 'src/app/core/services/tenantSetting/tenant-setting.service';
import { DialogWaComponent } from 'src/app/features/dialog/dialog-whatsapp/dialog-wa.component';

@Component({
	selector: 'app-dashboard-sms',
	templateUrl: './dashboard-sms.component.html',
	styleUrls: ['./dashboard-sms.component.scss'],
})
export class DashboardSmsComponent implements OnInit {
	screen: 'dashboard' | 'booking' | 'flight' | 'crew' | 'new' = 'dashboard';
	flightId: string;
	flight: Flight;
	bookingId: string;
	crewId: string;
	showAll: boolean = false;
	filter: string = 'all';
	filterActive: boolean = true;

	bookingConversations: any[];
	flightConversations: any[];
	crewConversations: any[];
	flightCrewIds: string[];
	conversations: any[];
	history = 0;
	historyBatch = 10;
	bookingConversationSubscription: Subscription;
	flightConversationSubscription: Subscription;
	crewConversationSubscription: Subscription;
	flightCrewSubscription: Subscription;
	// per-detail watchers, re-created on every navigation — must not accumulate (#165)
	private flightDetailSubscription: Subscription;
	private bookingDetailSubscription: Subscription;
	private crewDetailSubscription: Subscription;
	unreadSms: number;
	unreadSmsSubscription;
	whatsapp: WhatsappSettings;

	constructor(
		public dialog: MatDialog,
		private bookingService: BookingService,
		private crewService: CrewService,
		private smsService: SmsService,
		private router: Router,
		private flightService: FlightService,
		private tenantSettingService: TenantSettingService
	) {
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.updateCurrentUrl();
			}
		});
	}
	ngOnDestroy(): void {
		if (this.bookingConversationSubscription) this.bookingConversationSubscription.unsubscribe();
		if (this.flightConversationSubscription) this.flightConversationSubscription.unsubscribe();
		if (this.crewConversationSubscription) this.crewConversationSubscription.unsubscribe();
		if (this.flightCrewSubscription) this.flightCrewSubscription.unsubscribe();
		this.flightDetailSubscription?.unsubscribe();
		this.bookingDetailSubscription?.unsubscribe();
		this.crewDetailSubscription?.unsubscribe();
		if (this.unreadSmsSubscription) {
			this.unreadSmsSubscription.unsubscribe();
		}
	}

	ngOnInit(): void {
		this.tenantSettingService.getTenantSetting().valueChanges.subscribe((result) => {
			if (result.data.tenantSetting.whatsapp) {
				this.whatsapp = result.data.tenantSetting.whatsapp;
			}
		});
		this.unreadSmsSubscription = this.smsService.getUnreadSmsCount().subscribe({
			next: (count) => {
				this.updateUnreadCount(count);
			},
		});
		this.showMoreHistory();
		this.fetchCrewConversations();
		this.updateCurrentUrl();
	}

	private updateUnreadCount(globalCount: number): void {
		if (this.screen === 'dashboard') {
			this.unreadSms = globalCount;
			if (globalCount === 0 && this.filter === 'unread') {
				this.filter = 'all';
				this.showMoreHistory();
			}
		} else if (this.conversations && this.conversations.length > 0) {
			this.unreadSms = this.conversations.reduce((sum, c) => (sum += c?.smsUnread ?? 0), 0);
		} else {
			this.unreadSms = 0;
		}
	}

	private updateCurrentUrl(): void {
		const localUrl = this.router.url;
		this.screen = 'dashboard';
		this.filter = 'all';
		if (localUrl.includes('booking/') && !localUrl.includes('merge') && !localUrl.includes('new')) {
			this.screen = 'booking';
			this.filter = 'booking';
			this.bookingId = localUrl.split('/')[2];
		}
		if (localUrl.includes('flight/') && !localUrl.includes('new')) {
			this.screen = 'flight';
			this.filter = 'flight';
			this.flightId = localUrl.split('/')[2];
			this.showFlightConversations(this.flightId);
		}
		if (localUrl.includes('crew/') && !localUrl.includes('new')) {
			this.screen = 'crew';
			this.filter = 'crew';
			this.crewId = localUrl.split('/')[2];
		}
		if (localUrl.includes('new')) {
			this.screen = 'new';
			this.filter = 'all';
		}
		this.filterConversations();
	}

	toggleFilter() {
		if (this.filter === 'all') {
			this.showMoreHistory();
		} else {
			this.filterConversations();
		}
	}

	changeFilterActive(event) {
		this.filterActive = event;
	}

	private mergeAndSort(booking: any[], crew: any[]): any[] {
		const merged = [...(booking || []), ...(crew || [])];
		return merged.sort((a, b) => {
			const dateA = a?.smsLatest?.date ? new Date(a.smsLatest.date).getTime() : 0;
			const dateB = b?.smsLatest?.date ? new Date(b.smsLatest.date).getTime() : 0;
			return dateB - dateA;
		});
	}

	filterConversations() {
		if (this.filter === 'all') {
			this.conversations = this.mergeAndSort(this.bookingConversations, this.crewConversations);
		}
		if (this.filter === 'unread') {
			this.showUnread();
		}
		if (this.flightId && this.filter === 'flight') {
			const flightCrew = this.flightCrewIds && this.crewConversations
				? this.crewConversations.filter(c => this.flightCrewIds.includes(c.id))
				: [];
			this.conversations = this.mergeAndSort(this.flightConversations, flightCrew);
		}
		if (this.bookingId && this.filter === 'booking' && this.screen !== 'new') {
			this.showBookingConversations(this.bookingId);
		}
		if (this.crewId && this.filter === 'crew') {
			this.showCrewDetail(this.crewId);
		}
		if (this.screen !== 'dashboard' && this.conversations) {
			this.unreadSms = this.conversations.reduce((sum, c) => (sum += c?.smsUnread ?? 0), 0);
		}
	}

	showUnread() {
		if (this.bookingConversationSubscription) this.bookingConversationSubscription.unsubscribe();
		this.bookingConversationSubscription = this.bookingService.getBookingConversations(0, null, true).subscribe((bookingConversations) => {
			const unreadCrew = (this.crewConversations || []).filter(c => c?.smsUnread > 0);
			this.conversations = this.mergeAndSort(bookingConversations, unreadCrew);
		});
	}

	showMoreHistory() {
		if (this.bookingConversationSubscription) this.bookingConversationSubscription.unsubscribe();
		this.history += this.historyBatch;
		this.bookingConversationSubscription = this.bookingService.getBookingConversations(this.history).subscribe((bookingConversations) => {
			this.bookingConversations = bookingConversations;
			this.filterConversations();
		});
	}

	fetchCrewConversations() {
		if (this.crewConversationSubscription) this.crewConversationSubscription.unsubscribe();
		this.crewConversationSubscription = this.crewService.getCrewConversations(this.history).subscribe((crewConversations) => {
			this.crewConversations = crewConversations;
			this.filterConversations();
		});
	}

	showFlightConversations(flightId: string) {
		if (flightId) {
			if (this.flightConversationSubscription) this.flightConversationSubscription.unsubscribe();
			this.flightConversationSubscription = this.bookingService.getBookingConversations(0, flightId).subscribe((flightConversations) => {
				this.flightConversations = flightConversations;
				this.filterConversations();
			});
			this.flightDetailSubscription?.unsubscribe();
			this.flightDetailSubscription = this.flightService.get(flightId).valueChanges.subscribe((flightResult) => {
				this.flight = flightResult.data.flight;
			});
			if (this.flightCrewSubscription) this.flightCrewSubscription.unsubscribe();
			this.flightCrewSubscription = this.crewService.crewsPerFlight(flightId).valueChanges.subscribe((result) => {
				this.flightCrewIds = (result.data.crewsPerFlight || []).map((c: any) => c.id);
				this.filterConversations();
			});
		}
	}

	showBookingConversations(bookingId: string) {
		if (bookingId) {
			this.bookingDetailSubscription?.unsubscribe();
			this.bookingDetailSubscription = this.bookingService.getBooking(bookingId).valueChanges.subscribe((result) => {
				this.conversations = [result.data.booking];
			});
		}
	}

	showCrewDetail(crewId: string) {
		if (crewId) {
			this.crewDetailSubscription?.unsubscribe();
			this.crewDetailSubscription = this.crewService.get(crewId).valueChanges.subscribe((result) => {
				this.conversations = [result.data.crew];
			});
		}
	}

	// On a finished flight passengers are COMP, so the confirmed counter is always 0 —
	// the reminder must stay available for after-flight messages like photo sharing (#169)
	get reminderDisabled(): boolean {
		if (!this.whatsapp?.enabled) return true;
		if (this.flight?.status === FlightStatus.Fin) return !this.flight?.bookings?.list?.length;
		return this.flight?.passengerCounters?.passengersCountConfirmed === 0;
	}

	sendReminderWA() {
		this.dialog.open(DialogWaComponent, {
			// 2026-09-08: the send dialog may use more of the screen — the type selector and the
			// message columns were cramped in the old content-sized popup
			width: '720px',
			maxWidth: '95vw',
			maxHeight: '85vh',
			data: {
				all: true,
				smsTemplate: SmsType.ReminderSms,
				flight: this.flight,
				whatsapp: this.whatsapp,
			},
		});
	}
}
